import { useState } from "react";
import { Bell, BellRing, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEntityReminders } from "@/hooks/useEntityReminders";
import { useReminderAction } from "@/hooks/useReminders";
import { ReminderPicker } from "./ReminderPicker";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";

interface Props {
  entityType: "company" | "contact" | "deal" | "task" | "activity";
  entityId: string;
  entityName?: string;
  userTimezone: string;
}

export function EntityRemindersButton({ entityType, entityId, entityName, userTimezone }: Props) {
  const { data, isLoading } = useEntityReminders(entityType, entityId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { snooze, complete, cancel } = useReminderAction();
  const count = data?.count ?? 0;
  const reminders = data?.reminders ?? [];

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`relative ${count > 0 ? "text-primary-text" : "text-muted-foreground"}`}
            title={count > 0 ? `${count} reminder${count > 1 ? "s" : ""}` : "Set a reminder"}
          >
            {count > 0 ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full text-[10px] w-4 h-4 flex items-center justify-center">{count}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Reminders</h4>
            {isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
            {!isLoading && count === 0 && <p className="text-xs text-muted-foreground">No reminders for this {entityType}.</p>}
            {reminders.map((r: any) => {
              const localTime = toZonedTime(new Date(r.scheduled_for), userTimezone);
              return (
                <div key={r.id} className="border rounded-md p-2 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{r.title}</div>
                      <div className="text-xs text-muted-foreground">{format(localTime, "EEE MMM d, h:mm a")}</div>
                    </div>
                    <button onClick={() => cancel(r.id, "this")} className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => snooze(r.id, "1h")}>+1h</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => snooze(r.id, "1d")}>+1d</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => complete(r.id)}>Done</Button>
                  </div>
                </div>
              );
            })}
            <Button size="sm" className="w-full mt-2" onClick={() => setPickerOpen(true)}>Set new reminder</Button>
          </div>
        </PopoverContent>
      </Popover>

      <ReminderPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        userTimezone={userTimezone}
        defaults={{
          entityType,
          entityId,
          title: entityName ? `Follow up with ${entityName}` : "",
        }}
      />
    </>
  );
}
